import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { 
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  PhoneIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://sayhi-voice-engine-production.up.railway.app'

export default function RevenueAnalytics() {
  const { userBusinesses, isSuperAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [timeframe, setTimeframe] = useState('today')
  const [revenueData, setRevenueData] = useState(null)
  const [comparison, setComparison] = useState(null)
  const [error, setError] = useState(null)

  // Get the first business phone for this user (same pattern as MyBusiness)
  const myBusiness = userBusinesses?.[0]?.business_phone

  useEffect(() => {
    if (myBusiness) {
      fetchRevenueData()
    } else if (!isSuperAdmin()) {
      setLoading(false)
      setError('No business found')
    }
  }, [timeframe, myBusiness])

  const getAuthToken = () => {
    return localStorage.getItem('supabase_token')
  }

  const fetchRevenueData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const authToken = getAuthToken()
      
      if (!authToken) {
        throw new Error('No authentication token available')
      }
      
      if (!myBusiness) {
        throw new Error('No business phone found')
      }
      
      console.log('💰 Frontend: Loading revenue data for:', myBusiness)
      console.log('💰 Frontend: Using backend URL:', BACKEND_URL)
      
      // Fetch revenue analytics using business phone (not ID)
      const [analyticsResponse, comparisonResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/revenue/analytics/${myBusiness}?timeframe=${timeframe}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        }),
        fetch(`${BACKEND_URL}/api/dashboard/revenue/comparison/${myBusiness}?period=${timeframe === 'today' ? 'day' : timeframe}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        })
      ])

      console.log('📊 Frontend: Analytics response status:', analyticsResponse.status)
      console.log('📊 Frontend: Comparison response status:', comparisonResponse.status)

      if (!analyticsResponse.ok) {
        const errorText = await analyticsResponse.text()
        console.error('❌ Frontend: Analytics API error:', errorText)
        throw new Error(`Analytics API error: ${analyticsResponse.status}`)
      }
      
      const analyticsData = await analyticsResponse.json()
      const comparisonData = await comparisonResponse.json()

      console.log('📊 Frontend: Analytics data:', analyticsData)
      console.log('📊 Frontend: Comparison data:', comparisonData)

      if (analyticsData.success) {
        setRevenueData(analyticsData)
      } else {
        throw new Error(analyticsData.error || 'Failed to load revenue data')
      }
      
      if (comparisonData.success) {
        setComparison(comparisonData.comparison)
      }
      
    } catch (error) {
      console.error('❌ Frontend: Error fetching revenue data:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0)
  }

  const getConfidenceIcon = (confidence) => {
    switch(confidence) {
      case 'high':
        return <CheckCircleIconSolid className="h-5 w-5 text-green-600" />
      case 'medium':
        return <QuestionMarkCircleIcon className="h-5 w-5 text-yellow-600" />
      case 'low':
        return <XCircleIcon className="h-5 w-5 text-red-600" />
      default:
        return null
    }
  }

  const getConfidenceColor = (confidence) => {
    switch(confidence) {
      case 'high':
        return 'bg-green-100 text-green-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'low':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Redirect super admins to main dashboard
  if (isSuperAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Access</h2>
          <p className="text-gray-600 mb-4">Revenue analytics for all businesses is available in the main dashboard.</p>
          <a href="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Main Dashboard →</a>
        </div>
      </div>
    )
  }

  // No business access
  if (!myBusiness && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Business Access</h2>
          <p className="text-gray-600">You don't have access to any businesses. Contact your administrator.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading revenue analytics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <CurrencyDollarIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Analytics</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchRevenueData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const { stats, topServices, timeline, highValueCalls, confidenceBreakdown } = revenueData || {}

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <CurrencyDollarIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Revenue Analytics</h1>
                  <p className="text-sm text-gray-600">AI-powered revenue estimates from your calls</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex rounded-lg shadow-sm">
                {['today', 'week', 'month', 'year'].map((tf) => (
                  <button
                    key={tf}
                    onClick={() => setTimeframe(tf)}
                    className={`px-4 py-2 text-sm font-medium capitalize ${
                      timeframe === tf
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } ${tf === 'today' ? 'rounded-l-lg' : ''} ${tf === 'year' ? 'rounded-r-lg' : ''} border`}
                  >
                    {tf}
                  </button>
                ))}
              </div>
              
              <button
                onClick={fetchRevenueData}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm transition-all duration-200"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Total Potential</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats?.totalPotential)}</p>
                {comparison && (
                  <p className={`text-sm font-medium mt-1 flex items-center ${
                    comparison.change.direction === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {comparison.change.direction === 'up' ? 
                      <ArrowUpIcon className="h-4 w-4 mr-1" /> : 
                      <ArrowDownIcon className="h-4 w-4 mr-1" />
                    }
                    {Math.abs(comparison.change.percentage)}% vs last period
                  </p>
                )}
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Likely Revenue</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats?.likelyRevenue)}</p>
                <p className="text-sm text-gray-600">High + 50% Medium</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Conversion Rate</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.conversionRate || 0}%</p>
                <p className="text-sm text-purple-600">Scheduled appointments</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Avg per Call</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats?.averagePerCall)}</p>
                <p className="text-sm text-orange-600">Revenue potential</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Confidence Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Confidence Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-800 font-medium">High Confidence</span>
                <CheckCircleIconSolid className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(confidenceBreakdown?.high)}</p>
              <p className="text-sm text-green-700 mt-1">Appointments scheduled</p>
            </div>
            
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-yellow-800 font-medium">Medium Confidence</span>
                <QuestionMarkCircleIcon className="h-5 w-5 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-yellow-900">{formatCurrency(confidenceBreakdown?.medium)}</p>
              <p className="text-sm text-yellow-700 mt-1">Interested customers</p>
            </div>
            
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-red-800 font-medium">Low Confidence</span>
                <XCircleIcon className="h-5 w-5 text-red-600" />
              </div>
              <p className="text-2xl font-bold text-red-900">{formatCurrency(confidenceBreakdown?.low)}</p>
              <p className="text-sm text-red-700 mt-1">Just inquiring</p>
            </div>
          </div>
        </div>

        {/* Timeline Chart */}
        {timeline && timeline.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Timeline</h3>
            <div className="overflow-x-auto">
              <div className="flex space-x-2 pb-2">
                {timeline.map((point, index) => (
                  <div key={index} className="flex flex-col items-center min-w-[80px]">
                    <div 
                      className="w-full bg-blue-600 rounded-t"
                      style={{ height: `${Math.max((point.revenue / Math.max(...timeline.map(t => t.revenue))) * 150, 10)}px` }}
                    ></div>
                    <p className="text-xs text-gray-600 mt-2">{formatCurrency(point.revenue)}</p>
                    <p className="text-xs text-gray-500">{point.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Top Services */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Services by Revenue</h3>
          <div className="space-y-3">
            {!topServices || topServices.length === 0 ? (
              <div className="text-center py-8">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No service data available yet</p>
              </div>
            ) : (
              topServices.map((service, index) => (
                <div key={index} className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors">
                  <div>
                    <p className="font-medium text-gray-900">{service.service}</p>
                    <p className="text-sm text-gray-600">
                      {service.count} calls • Avg: {formatCurrency(service.averageValue)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(service.revenue)}</p>
                    {service.highConfidence > 0 && (
                      <p className="text-sm text-green-600">
                        {service.highConfidence} confirmed
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent High-Value Calls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recent High-Value Calls</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {!highValueCalls || highValueCalls.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <PhoneIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No high-value calls recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-600">
                      <th className="px-6 py-3">Time</th>
                      <th className="px-6 py-3">Customer</th>
                      <th className="px-6 py-3">Service</th>
                      <th className="px-6 py-3">Confidence</th>
                      <th className="px-6 py-3 text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {highValueCalls.map((call) => (
                      <tr key={call.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(call.time).toLocaleString([], { 
                            month: 'short', 
                            day: 'numeric', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{call.customerPhone}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{call.service || 'General Inquiry'}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(call.confidence)}`}>
                            {call.confidence}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                          {formatCurrency(call.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}